import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bed, Phone, Mail, ExternalLink, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AccommodationPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccommodations = async () => {
      try {
        const res = await axios.get(`${API}/accommodations`);
        setAccommodations(res.data);
      } catch (error) {
        console.error('Error fetching accommodations:', error);
        setAccommodations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAccommodations();
  }, []);

  return (
    <div data-testid="accommodation-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Bed className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('accommodation')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Dove dormire a Caltavuturo e dintorni.'
                : 'Where to stay in Caltavuturo and surroundings.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accommodations List */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : accommodations.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {accommodations.map((acc, i) => (
                <motion.div
                  key={acc.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border rounded-lg overflow-hidden group hover:border-[#FF6D00]/50 transition-colors"
                >
                  {/* Image */}
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={acc.images?.[0] || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600'}
                      alt={acc.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-xl uppercase mb-3">{acc.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {getTranslatedContent(acc.description)}
                    </p>

                    <ul className="space-y-2 mb-4">
                      {acc.address && (
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                          {acc.address}
                        </li>
                      )}
                      {acc.contact_phone && (
                        <li className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-[#FF6D00]" />
                          <a href={`tel:${acc.contact_phone}`} className="text-gray-600 hover:text-[#FF6D00]">
                            {acc.contact_phone}
                          </a>
                        </li>
                      )}
                      {acc.contact_email && (
                        <li className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-[#FF6D00]" />
                          <a href={`mailto:${acc.contact_email}`} className="text-gray-600 hover:text-[#FF6D00]">
                            {acc.contact_email}
                          </a>
                        </li>
                      )}
                    </ul>

                    {acc.website && (
                      <a 
                        href={acc.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#FF6D00] font-semibold text-sm hover:underline"
                      >
                        {language === 'it' ? 'Visita il sito' : 'Visit website'}
                        <ExternalLink className="w-4 h-4 ml-1" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Note */}
          <div className="mt-16 text-center">
            <p className="text-gray-500 text-sm">
              {language === 'it'
                ? 'Vuoi segnalare una struttura? Contattaci!'
                : 'Want to suggest an accommodation? Contact us!'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
